import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Skeleton } from '../ui/skeleton';
import { Switch } from '../ui/switch';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../ui/table';
import {
  Webhook, KeyRound, Loader2, RefreshCw, PlugZap, CheckCircle2, XCircle,
  Trash2, Info, Tag,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const fmtDateTime = (v) => (v ? new Date(v).toLocaleString() : '—');

const EVENT_LABELS = {
  lead: 'Someone signs up (lead)',
  trial_started: 'Free trial started',
  paid: 'Became a paying customer',
  cancel_pending: 'Scheduled a cancellation',
  cancelled: 'Membership fully cancelled',
  payment_failed: 'Payment failed (past due)',
};

export default function OwnerAdminGhl() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [config, setConfig] = useState(null);
  const [log, setLog] = useState([]);

  const [mode, setMode] = useState('webhook');
  const [locationId, setLocationId] = useState('');
  const [token, setToken] = useState('');
  const [webhookUrl, setWebhookUrl] = useState('');
  const [enabled, setEnabled] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [cRes, lRes] = await Promise.all([
        fetch(`${API_URL}/api/owner-admin/ghl/config`, { credentials: 'include' }),
        fetch(`${API_URL}/api/owner-admin/ghl/log?limit=50`, { credentials: 'include' }),
      ]);
      if (cRes.ok) {
        const c = await cRes.json();
        setConfig(c);
        if (c.configured) {
          setMode(c.mode || 'webhook');
          setLocationId(c.location_id || '');
          setEnabled(c.enabled !== false);
        }
      }
      if (lRes.ok) {
        const l = await lRes.json();
        setLog(l.entries || []);
      }
    } catch (e) {
      console.error(e);
      toast.error('Failed to load GoHighLevel settings');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      const body = { mode, enabled };
      if (mode === 'pit') {
        body.location_id = locationId.trim();
        if (token.trim()) body.token = token.trim();
      } else {
        body.webhook_url = webhookUrl.trim();
      }
      const res = await fetch(`${API_URL}/api/owner-admin/ghl/config`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(body),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        toast.success('GoHighLevel integration saved');
        setToken('');
        setWebhookUrl('');
        load();
      } else {
        toast.error(data.detail || 'Failed to save');
      }
    } catch (e) {
      toast.error('Failed to save integration');
    } finally {
      setSaving(false);
    }
  };

  const runTest = async () => {
    setTesting(true);
    try {
      const res = await fetch(`${API_URL}/api/owner-admin/ghl/test`, {
        method: 'POST', credentials: 'include',
      });
      const data = await res.json().catch(() => ({}));
      if (data.ok) toast.success(data.message || 'Connection OK');
      else toast.error(data.detail || `Test failed (${data.status_code || '?'})`);
    } catch (e) {
      toast.error('Test failed');
    } finally {
      setTesting(false);
    }
  };

  const disable = async () => {
    if (!window.confirm('Remove the GoHighLevel link? Lifecycle events will stop syncing.')) return;
    try {
      const res = await fetch(`${API_URL}/api/owner-admin/ghl/config`, {
        method: 'DELETE', credentials: 'include',
      });
      if (res.ok) { toast.success('GoHighLevel link removed'); load(); }
      else toast.error('Failed to remove');
    } catch (e) {
      toast.error('Failed to remove');
    }
  };

  const eventTags = config?.event_tags || {};

  return (
    <div className="space-y-6" data-testid="ghl-integration-page">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <PlugZap className="h-6 w-6 text-emerald-400" />
            GoHighLevel Sync
          </h1>
          <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
            Link CountBucks to GoHighLevel <strong>once</strong>. CountBucks keeps owning billing,
            memberships & cancellations (Stripe) and automatically tags your GHL contacts by email
            as they move through the membership lifecycle. Every funnel you build in GHL afterwards
            works automatically — no per-funnel setup.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {config?.configured && (
            <Badge variant="outline" className={config.enabled
              ? 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30'
              : 'bg-muted/20 text-muted-foreground border-white/10'}>
              {config.enabled ? 'Active' : 'Paused'}
            </Badge>
          )}
          <Button variant="outline" size="sm" onClick={load} data-testid="ghl-refresh-btn">
            <RefreshCw className="h-4 w-4 mr-2" /> Refresh
          </Button>
        </div>
      </div>

      {loading ? (
        <Skeleton className="h-64 w-full" />
      ) : (
        <>
          {/* Config card */}
          <Card>
            <CardHeader>
              <CardTitle className="text-white">Connection</CardTitle>
              <CardDescription>
                Choose how CountBucks talks to your GoHighLevel sub-account.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-5">
              <div className="grid gap-2 max-w-md">
                <Label>Sync method</Label>
                <Select value={mode} onValueChange={setMode}>
                  <SelectTrigger data-testid="ghl-mode-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="webhook">
                      <span className="flex items-center gap-2"><Webhook className="h-4 w-4" /> Inbound Webhook (easiest)</span>
                    </SelectItem>
                    <SelectItem value="pit">
                      <span className="flex items-center gap-2"><KeyRound className="h-4 w-4" /> Private Integration Token (tags contacts directly)</span>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {mode === 'webhook' ? (
                <div className="grid gap-2 max-w-2xl">
                  <Label htmlFor="ghl-webhook">GoHighLevel Inbound Webhook URL</Label>
                  <Input
                    id="ghl-webhook"
                    data-testid="ghl-webhook-input"
                    placeholder="https://services.leadconnectorhq.com/hooks/..."
                    value={webhookUrl}
                    onChange={(e) => setWebhookUrl(e.target.value)}
                  />
                  <p className="text-xs text-muted-foreground">
                    In GHL: Automation → Workflows → new Workflow → trigger <em>Inbound Webhook</em>,
                    copy the generated URL and paste it here.
                    {config?.webhook_url_masked && (
                      <span className="block mt-1 text-emerald-400/80">Saved: {config.webhook_url_masked}</span>
                    )}
                  </p>
                </div>
              ) : (
                <div className="grid gap-4 max-w-2xl sm:grid-cols-2">
                  <div className="grid gap-2">
                    <Label htmlFor="ghl-location">Location ID</Label>
                    <Input
                      id="ghl-location"
                      data-testid="ghl-location-input"
                      placeholder="abcd1234LocationId"
                      value={locationId}
                      onChange={(e) => setLocationId(e.target.value)}
                    />
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="ghl-token">Private Integration Token</Label>
                    <Input
                      id="ghl-token"
                      data-testid="ghl-token-input"
                      type="password"
                      placeholder={config?.has_token ? '•••••••• (saved — leave blank to keep)' : 'pit-xxxxxxxx'}
                      value={token}
                      onChange={(e) => setToken(e.target.value)}
                    />
                  </div>
                  <p className="text-xs text-muted-foreground sm:col-span-2">
                    In GHL: Settings → Private Integrations → create one with the <code>contacts.write</code> scope.
                  </p>
                </div>
              )}

              <div className="flex items-center gap-3">
                <Switch checked={enabled} onCheckedChange={setEnabled} data-testid="ghl-enabled-switch" />
                <span className="text-sm text-muted-foreground">Sync enabled</span>
              </div>

              <div className="flex items-center gap-3 flex-wrap pt-2">
                <Button onClick={save} disabled={saving} data-testid="ghl-save-btn">
                  {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                  Save integration
                </Button>
                <Button variant="outline" onClick={runTest} disabled={testing || !config?.configured} data-testid="ghl-test-btn">
                  {testing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <PlugZap className="h-4 w-4 mr-2" />}
                  Test connection
                </Button>
                {config?.configured && (
                  <Button variant="ghost" onClick={disable} className="text-red-400 hover:text-red-300" data-testid="ghl-remove-btn">
                    <Trash2 className="h-4 w-4 mr-2" /> Remove link
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Lifecycle → tags reference */}
          <Card>
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Tag className="h-5 w-5 text-emerald-400" /> What gets synced
              </CardTitle>
              <CardDescription>
                These lifecycle events fire automatically. Build GHL automations off these tags once —
                they apply to every funnel.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid gap-3 sm:grid-cols-2">
                {Object.entries(eventTags).map(([event, tags]) => (
                  <div key={event} className="rounded-lg border border-white/10 bg-white/[0.03] p-3" data-testid={`ghl-event-${event}`}>
                    <p className="text-sm font-medium text-white">{EVENT_LABELS[event] || event}</p>
                    <div className="flex flex-wrap gap-1.5 mt-2">
                      {(tags.add || []).map((t) => (
                        <Badge key={t} variant="outline" className="bg-emerald-500/10 text-emerald-300 border-emerald-500/25 text-xs">
                          +{t}{event === 'paid' ? ' · cb-paid-{plan}' : ''}
                        </Badge>
                      ))}
                      {(tags.remove || []).map((t) => (
                        <Badge key={t} variant="outline" className="bg-red-500/10 text-red-300/80 border-red-500/25 text-xs">
                          −{t}
                        </Badge>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-4 flex items-start gap-2 text-xs text-muted-foreground rounded-lg bg-white/[0.03] border border-white/10 p-3">
                <Info className="h-4 w-4 mt-0.5 shrink-0 text-emerald-400" />
                <span>
                  Contacts are matched by <strong>email</strong>, so a lead who fills out any GHL funnel and later
                  subscribes in CountBucks is the same contact — their tags update in place. To manage a
                  cancellation, do it in CountBucks; GHL receives <code>cb-cancel-pending</code> / <code>cb-cancelled</code> automatically.
                </span>
              </div>
            </CardContent>
          </Card>

          {/* Sync log */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-white">Recent sync activity</CardTitle>
                  <CardDescription>
                    {config?.log_count || 0} total events · {config?.error_count || 0} errors
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {log.length === 0 ? (
                <p className="text-sm text-muted-foreground py-6 text-center">
                  No sync activity yet. Events will appear here as people sign up, subscribe, or cancel.
                </p>
              ) : (
                <Table data-testid="ghl-log-table">
                  <TableHeader>
                    <TableRow>
                      <TableHead>When</TableHead>
                      <TableHead>Contact</TableHead>
                      <TableHead>Event</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Detail</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {log.map((row, i) => (
                      <TableRow key={i}>
                        <TableCell className="text-xs text-muted-foreground whitespace-nowrap">{fmtDateTime(row.created_at)}</TableCell>
                        <TableCell className="text-sm">{row.email || '—'}</TableCell>
                        <TableCell className="text-sm">
                          {EVENT_LABELS[row.event] || row.event}
                          {row.plan ? <span className="text-muted-foreground"> · {row.plan}</span> : null}
                        </TableCell>
                        <TableCell>
                          {row.status === 'success' ? (
                            <span className="flex items-center gap-1 text-emerald-400 text-xs"><CheckCircle2 className="h-3.5 w-3.5" /> success</span>
                          ) : row.status === 'skipped' ? (
                            <span className="text-muted-foreground text-xs">skipped</span>
                          ) : (
                            <span className="flex items-center gap-1 text-red-400 text-xs"><XCircle className="h-3.5 w-3.5" /> error</span>
                          )}
                        </TableCell>
                        <TableCell className="text-xs text-muted-foreground max-w-[240px] truncate">
                          {row.error || (row.tags_added ? `+${(row.tags_added || []).join(', ')}` : row.reason || '—')}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
